import React, { useEffect, useState } from "react";
import CustomModel from "../../Utility/CustomModel";
import { Editor } from "@tinymce/tinymce-react";
import { icons } from "../../Utility/RenderButtonLists";

const Affiliation = ({ handleCancel, editor, show }) => {
  const [affiliationData, setAffiliationData] = useState([]);

  useEffect(() => {
    if (!editor) return;
    const affs = editor.dom.select("aff");

    const parsed = affs.map((affElement, index) => {
      const children = Array.from(affElement.children);
      return {
        index,
        tag: affElement.tagName,
        children: children.map((child) => ({
          tag: child.tagName.toLowerCase(),
          text: child.textContent,
          attributes: Object.fromEntries(
            [...child.attributes].map((attr) => [attr.name, attr.value])
          ),
        })),
      };
    });

    setAffiliationData(parsed);
  }, [editor]);

  const relabelAffs = (affArray) => {
    return affArray.map((aff, i) => {
      const updatedChildren = aff.children.map((child) => {
        if (child.tag.toLowerCase() === "label") {
          return {
            ...child,
            text: ` ${i + 1} `,
          };
        }
        return child;
      });

      return {
        ...aff,
        children: updatedChildren,
      };
    });
  };

  const handleChange = (rowIndex, tag, value) => {
    setAffiliationData((prev) =>
      prev.map((item, i) => {
        if (i !== rowIndex) return item;
        const children = [...item.children];
        const childIndex = children.findIndex((c) => c.tag === tag);
        if (childIndex > -1) {
          children[childIndex].text = value;
        } else {
          children.push({ tag, text: value, attributes: {} });
        }
        return { ...item, children };
      })
    );
  };

  const handleAddRow = (index) => {
    const newAff = {
      index: affiliationData.length,
      tag: "AFF",
      children: [
        { tag: "label", text: index + 2, attributes: {} },
        { tag: "institution", text: "", attributes: {} },
        { tag: "addr-line", text: "", attributes: {} },
        { tag: "country", text: "", attributes: {} },
      ],
    };

    const updated = [...affiliationData];
    updated.splice(index + 1, 0, newAff);
    const updatedData = relabelAffs(updated);
    setAffiliationData(updatedData);
  };

  const handleRemoveRow = (i) => {
    const updated = [...affiliationData];
    const newUpdated = updated.filter((_, idx) => idx !== i);
    const updatedData = relabelAffs(newUpdated);
    setAffiliationData(updatedData);
  };

  // Submit changes back to the editor DOM
  // const handleSubmit = () => {
  //   if (!editor) return;

  //   const contribGroup = editor.dom.select("contrib-group")[0];
  //   if (!contribGroup) return;

  //   const existingAffs = editor.dom.select("contrib-group > aff");

  //   affiliationData.forEach((data, index) => {
  //     let affNode = existingAffs[index];

  //     // If aff already exists, update it
  //     if (affNode) {
  //       // console.log(affNode.firstChild);

  //       while (affNode.firstChild) affNode.removeChild(affNode.firstChild);

  //       data.children.forEach((child) => {
  //         const childNode = editor.dom.create(
  //           child.tag,
  //           child.attributes,
  //           child.text
  //         );
  //         affNode.appendChild(childNode);
  //       });
  //     } else {
  //       // Create new aff
  //       const newAff = editor.dom.create("aff");

  //       data.children.forEach((child) => {
  //         const childNode = editor.dom.create(
  //           child.tag,
  //           child.attributes,
  //           child.text
  //         );
  //         newAff.appendChild(childNode);
  //       });

  //       // Insert after previous aff if present, else append
  //       const prevAff = existingAffs[index - 1];
  //       if (prevAff && prevAff.nextSibling) {
  //         contribGroup.insertBefore(newAff, prevAff.nextSibling);
  //       } else {
  //         contribGroup.appendChild(newAff);
  //       }
  //     }
  //   });

  //   // Remove extra affs if any
  //   const allAffs = editor.dom.select("contrib-group > aff");
  //   for (let i = affiliationData.length; i < allAffs.length; i++) {
  //     allAffs[i]?.remove();
  //   }

  //   handleCancel();
  // };

  const handleAffliationChange = () => {
    const contribGroup = editor.dom.select("contrib-group")[0];
    let refCits = contribGroup.querySelectorAll("xref");

    refCits.forEach((cit) => {
      const oldRid = editor.dom.getAttrib(cit, "rid")?.trim();
      const matchingAff = editor.dom.select(`aff[tid="${oldRid}"]`)[0];
      //  const removedAff = editor.dom.select(`aff[id="${oldRid}"]`)[0];
      //  const removeAff = editor.dom.select(`aff[tid="${oldRid}"]`)[0];
      const removedAffArray = editor.dom.select(`aff[id="${oldRid}"]`);
      const removeAffArray = editor.dom.select(`aff[tid="${oldRid}"]`);

      let newId = oldRid;

      if (matchingAff) {
        newId = editor.dom.getAttrib(matchingAff, "id");

        const newNum = newId.split("-")[1];
        // Extract number from ref-id
        editor.dom.setAttrib(cit, "rid", newId);
        editor.dom.setAttrib(cit, "href", `#${newId}`);
        editor.dom.setAttrib(cit, "data-mce-href", `#${newId}`);
        editor.dom.setHTML(cit, `${newNum}`); // Display the number in xref
      }

      if (removedAffArray.length === 0 && removeAffArray.length === 0) {
        editor.dom.remove(cit);
      }
    });

    const affs = contribGroup.querySelectorAll("aff");

    affs.forEach((aff) => {
      if (aff.hasAttribute("tid")) {
        aff.removeAttribute("tid");
      }
    });
  };

  const handleSubmit = () => {
    if (!editor) return;

    const contribGroup = editor.dom.select("contrib-group")[0];
    if (!contribGroup) return;

    const existingAffs = contribGroup.querySelectorAll("aff");

    const validAffIds = affiliationData.map(
      (data) => `aff-${data.children[0].text.trim()}`
    );

    existingAffs.forEach((aff) => {
      const affId = editor.dom.getAttrib(aff, "id");
      if (!validAffIds.includes(affId)) {
        aff.parentNode.removeChild(aff);
      }
    });
    affiliationData.forEach((data, index) => {
      const affOldId = `aff-${data.index + 1}`;
      const affNewId = `aff-${data.children[0].text.trim()}`;
      const affNode = editor.dom.select(`#${affNewId}`)[0];

      // If aff already exists, update it
      if (affNode) {
        while (affNode.firstChild) affNode.removeChild(affNode.firstChild);

        data.children.forEach((child) => {
          const childNode = editor.dom.create(
            child.tag,
            child.attributes,
            child.text
          );
          affNode.appendChild(childNode);
          affNode.setAttribute("tid", affOldId);
        });
      } else {
        // Create new aff with incremented id

        const newAffId = `aff-${
          existingAffs.length + (index - existingAffs.length) + 1
        }`;
        const newAff = editor.dom.create("aff", { id: newAffId });

        data.children.forEach((child) => {
          const childNode = editor.dom.create(
            child.tag,
            child.attributes,
            child.text
          );
          newAff.appendChild(childNode);
          newAff.setAttribute("tid", affOldId);
        });

        // Insert after last existing aff or append at end
        const lastAff = existingAffs[existingAffs.length - 1];
        if (lastAff && lastAff.nextSibling) {
          contribGroup.insertBefore(newAff, lastAff.nextSibling);
        } else {
          contribGroup.appendChild(newAff);
        }
      }
    });

    handleAffliationChange();
    handleCancel();
  };

  const handleMoveRow = (i) => {
    const cloneData = [...affiliationData];

    if (i < cloneData.length - 1) {
      [cloneData[i], cloneData[i + 1]] = [cloneData[i + 1], cloneData[i]];

      cloneData.forEach((item, index) => {
        item.children[0].text = `${index + 1}`;
      });
      const updatedData = relabelAffs(cloneData);
      setAffiliationData(updatedData);
    }
  };

  return (
    <div className="w-fit h-fit">
      <CustomModel
        handleCancel={handleCancel}
        heading="Affiliation"
        width="75vw"
        show={show}
      >
        <div className="flex flex-col">
          <div className="flex flex-col overflow-auto w-full max-h-[500px]">
            {affiliationData.map((data, i) => {
              const tagMap = Object.fromEntries(
                data.children.map((child) => [child.tag, child.text])
              );

              return (
                <div key={i} className="flex w-full gap-2 text-[13px] p-2">
                  {["label", "institution", "addr-line", "country"].map(
                    (field) => (
                      <div key={field} className="flex w-full flex-col gap-2 ">
                        <label>{field}</label>
                        <input
                          title={tagMap[field] || ""}
                          className="border-[1px] border-grey py-[2px]"
                          type="text"
                          value={tagMap[field] || ""}
                          onChange={(e) =>
                            handleChange(i, field, e.target.value)
                          }
                          disabled={field === "label"}
                        />
                      </div>
                    )
                  )}

                  <div className="flex justify-center items-center  pt-4 mt-2 gap-2">
                    <button
                      className="px-4 border border-grey py-[2px] min-w-[100px]"
                      onClick={() => handleAddRow(i)}
                    >
                      Add Row
                    </button>
                    <button
                      className="px-4 border border-grey py-[2px] min-w-[100px]"
                      onClick={() => handleRemoveRow(i)}
                      disabled={affiliationData.length === 1}
                    >
                      Delete Row
                    </button>
                    <button
                      className="px-2 py-[2px] border border-grey"
                      onClick={() => handleMoveRow(i)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="icon icon-tabler icons-tabler-outline icon-tabler-switch-vertical"
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M3 8l4 -4l4 4" />
                        <path d="M7 4l0 9" />
                        <path d="M13 16l4 4l4 -4" />
                        <path d="M17 10l0 10" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="ml-auto">
            <div className="flex py-3 gap-4">
              <button
                className="px-[15px] py-1 rounded-[8px] hover:text-[#20c77c] border border-grey hover:border-[#20c77c]"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                className="px-[15px] py-1 bg-[#00b96b] text-white rounded-[8px] hover:bg-hoverbtnbg"
                onClick={handleSubmit}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </CustomModel>
    </div>
  );
};

// const Affiliation = ({ handleCancel, editor }) => {
//   const [affData, setAffData] = useState("");
//   const [rawXml, setRawXml] = useState("");
//   const [htmlContent, setHtmlContent] = useState("");

//   const xmlTags = [
//     "title",
//     "title1",
//     "article",
//     "front",
//     "issue",
//     "fpage",
//     "lpage",
//     "history",
//     "abstract",
//     "sec",
//     "label",
//     "fig",
//     "aff",
//     "author-notes",
//     "pub-date",
//     "volume",
//     "contrib-group",
//     "permissions",
//     "copyright-statement",
//     "copyright-year",
//     "copyright-holder",
//     "license",
//     "license-p",
//     "kwd-group",
//     "body",
//     "list",
//     "list-item",
//     "table-wrap",
//     "ack",
//     "fn-group",
//     "fn",
//     "ref-list",
//     "back",
//     "mixed-citation",
//     "person-group",
//     "string-name",
//     "given-names",
//     "article-title",
//     "pub-id",
//     "ref",
//     "source1",
//   ];

//   useEffect(() => {
//     const contribgroup = editor?.dom?.select("contrib-group");
//     if (contribgroup && contribgroup.length > 0) {
//       setRawXml(contribgroup[0].innerHTML);
//     }
//   }, [editor]);

//   useEffect(() => {
//     if (!rawXml) return;

//     // Wrap in a dummy root for parsing fragment
//     const wrappedXml = `<root>${rawXml}</root>`;
//     const parser = new DOMParser();
//     const xmlDoc = parser.parseFromString(wrappedXml, "application/xml");

//     const convertNode = (node) => {
//       if (node.nodeType === 3) return node.textContent; // Text node

//       if (node.nodeType === 1) {
//         const tag = node.tagName;
//         const childrenHtml = Array.from(node.childNodes)
//           .map(convertNode)
//           .join("");
//         return `<span class="${tag}" data-xml-tag="${tag}">${childrenHtml}</span>`;
//       }
//       return "";
//     };

//     const convertedHtml = Array.from(xmlDoc.documentElement.childNodes)
//       .map(convertNode)
//       .join("");

//     setHtmlContent(convertedHtml);
//   }, [rawXml]);

//   const afftags = editor?.dom?.select("contrib-group > aff");

//   console.log(afftags[0].children);

//   const handleChange = (newcontent) => {
//     setHtmlContent(newcontent);
//   };

//   return (
//     <div>
//       <CustomModel handleCancel={handleCancel} heading={"Affiliation"}>
//         <Editor
//           value={htmlContent}
//           onEditorChange={(content) => handleChange(content)}
//           init={{
//             content_css: ["/vedas/custom.css"],
//             promotion: false,
//             branding: false,
//             selector: "textarea",
//             height: "500px",
//             width: "100%",
//             // valid_elements: "[id]",
//             custom_elements: xmlTags.join(","),
//             // extended_valid_elements : "title,title1,article,front,issue,fpage,lpage,history,abstract,sec,label,fig,aff,author-notes,pub-date,volume,contrib-group,permissions,copyright-statement,copyright-year,copyright-holder,license,license-p,kwd-group,body,list,list-item,table-wrap,ack,fn-group,fn,ref-list,back,ref-list,mixed-citation,person-group,string-name,given-names,article-title,pub-id,ref,source1",
//             remove_linebreaks: false,
//             extended_valid_elements: xmlTags
//               .map((tag) => `${tag}[*]`)
//               .join(","),

//             menubar: false,
//             encoding: "xml",
//             allow_script_urls: true,
//             verify_html: false,
//           }}
//         />
//       </CustomModel>
//     </div>
//   );
// };

export default Affiliation;
